import { useMemo, useState } from 'react'
import { Box, Flex, SegmentedControl, Table } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { GoalStatus, Order } from '@/types/actionPlan.types'

import { TableList } from '@/modules/actionPlan/pages/components/TableList/TableList'
import { TableOptions } from '@/modules/actionPlan/pages/components/TableOptions/TableOptions'
import { TableTitle } from '@/modules/actionPlan/pages/components/TableTitle/TableTitle'

import { Loader } from '@/components/Loader/Loader'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'

import { useGoalsServiceGoalsControllerGetAll } from '@/__generated__/queries'
import * as Common from '@/__generated__/queries/common'
import { AdvisorsService } from '@/__generated__/requests'

import classes from './ActionPlanTable.module.css'

const STATUS_TABS = [
  { label: 'Active', value: GoalStatus.ACTIVE },
  { label: 'Completed', value: GoalStatus.COMPLETED },
  { label: 'Archived', value: GoalStatus.ARCHIVED },
]

export function ActionPlanTable({ userId }: { userId?: number }) {
  const [order, setOrder] = useState<Order>(Order.DEADLINE)
  const [status, setStatus] = useState<GoalStatus>(GoalStatus.ACTIVE)

  const isAdvisor = !!userId

  const userQuery = useGoalsServiceGoalsControllerGetAll(
    { orderBy: order, status },
    undefined,
    { enabled: !isAdvisor },
  )

  const advisorQuery = useQuery({
    enabled: isAdvisor,
    queryFn: () =>
      AdvisorsService.advisorUsersControllerGetGoals({ userId: userId! }),
    queryKey: Common.UseAdvisorsServiceAdvisorUsersControllerGetGoalsKeyFn({
      userId: userId!,
    }),
  })

  const { data, isFetching, isLoading } = isAdvisor ? advisorQuery : userQuery

  const handleFilterChange = (value: Order) => {
    setOrder(value)
  }

  const goals = useMemo(() => {
    if (!data) return []

    if (!isAdvisor) return data

    const filtered = data.filter(g => g.status === status)

    return [...filtered].sort((a, b) => {
      if (order === Order.DEADLINE)
        return dayjs(a.dueDate).diff(dayjs(b.dueDate))
      if (order === Order.NEW_FIRST)
        return dayjs(b.startDate).diff(dayjs(a.startDate))

      return dayjs(a.startDate).diff(dayjs(b.startDate))
    })
  }, [data, isAdvisor, order, status])

  if (isFetching || isLoading) {
    return (
      <Flex mb={24}>
        <Loader isMinimized />
      </Flex>
    )
  }

  return (
    <Box className={classes.table}>
      <TableOptions
        onFilterChange={handleFilterChange}
        order={order}
        readOnly={isAdvisor}
      />

      <Flex px={24} py={12}>
        <SegmentedControl
          data={STATUS_TABS}
          onChange={value => setStatus(value as GoalStatus)}
          value={status}
        />
      </Flex>

      <ScrollArea scrollbars="x">
        <Table>
          {!!goals.length && <TableTitle />}
          <TableList items={goals} readOnly={isAdvisor} status={status} />
        </Table>
      </ScrollArea>
    </Box>
  )
}
